import { FC, useState, ChangeEvent, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useSWR, { mutate } from 'swr';
import Mentorship from 'types/Mentorship';
// Utilisation du bon chemin et de l'export par défaut identifié
import privateAPI from 'utils/privateAxiosInstance';

import Button from 'components/Button';
import Select from 'components/form/Inputs/Select';
import Textarea from 'components/form/Inputs/Textarea';
import Input from 'components/form/Inputs/Input';

const DeclarePastInterview: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Récupération des détails du mentorat pour avoir le menteeId
  const { data: mentorship } = useSWR<Mentorship>(
    `/mentorship/mentor/mentee-details/${id}`
  );

  const [loading, setLoading] = useState(false);
  const [state, setState] = useState({
    date: new Date().toISOString().split('T')[0],
    type: 'PHONE',
    content: '',
    progressComment: '',
    qualityComment: '',
    needs: '',
    notes: '',
  });

const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!mentorship?.menteeId) {
      console.error("Impossible d'enregistrer : menteeId manquant.");
      return;
    }

    setLoading(true);

    const payload = {
      ...state,
      menteeId: mentorship.menteeId,
      // On s'assure d'envoyer MEETJIT si VISIO était sélectionné
      type: state.type === 'VISIO' ? 'MEETJIT' : state.type 
    };

    try {
      await privateAPI.post('/mentor-interview-review/past', payload);

      const redirectPath = `/mentor/mentorship/management/mentees/details/${id}/interviews/`;
      
      // Rafraîchir la liste et naviguer
      await mutate((key: string) => typeof key === 'string' && key.startsWith(`/interview/mentorship/${id}`));
      navigate(redirectPath);

    } catch (error: any) {
      if (error?.response?.status === 401) {
        alert("Votre session a expiré. Veuillez vous reconnecter.");
      } else {
        console.error("Erreur lors de l'enregistrement :", error);
        alert("Une erreur est survenue lors de l'enregistrement.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
      <h2 style={{ marginBottom: '20px' }}>
        Déclarer un entretien passé avec {mentorship?.mentee?.firstName} {mentorship?.mentee?.lastName}
      </h2>
      
      <div style={{ display: 'flex', gap: '2rem', marginBottom: '2rem' }}>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Date de l'entretien</label>
          <Input 
            type="date" 
            value={state.date} 
            onChange={(e: ChangeEvent<HTMLInputElement>) => setState({...state, date: e.target.value})}
          />
        </div>
        
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Type d'entretien</label>
          <Select 
            value={state.type} 
            onChange={(e: ChangeEvent<HTMLSelectElement>) => setState({...state, type: e.target.value})}
          >
            <option value="PHONE">Téléphone</option>
            <option value="IRL">En face à face</option>
            <option value="MEETJIT">Visioconférence</option>
            <option value="OTHER">Autre</option>
          </Select>
        </div>
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Contenu de l'échange</label>
        <Textarea
          placeholder="Description du contenu de l'échange"
          rows={5}
          value={state.content}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setState({...state, content: e.target.value})}
          characterLimit={2000}
          noEnterFireSubmit
        />
      </div>
      
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Progression</label>
        <Textarea
          placeholder="Suivi de la progression"
          rows={8}
          value={state.progressComment}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setState({...state, progressComment: e.target.value})}
          characterLimit={2000}
          noEnterFireSubmit
        />
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Mes échanges</label>
        <Textarea
          placeholder="Ce que je retiens de nos échanges"
          rows={8}
          value={state.qualityComment}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setState({...state, qualityComment: e.target.value})}
          characterLimit={2000}
          noEnterFireSubmit
        />
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Prise de note</label>
        <Textarea
          placeholder="Notes"
          rows={8}
          value={state.notes}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setState({...state, notes: e.target.value})}
          characterLimit={2000}
          noEnterFireSubmit
        />
      </div>

      <div style={{ marginTop: '2.5rem', display: 'flex', gap: '1rem' }}>
        <Button type="submit" color="primary" disabled={loading || !mentorship}>
          {loading ? 'Enregistrement...' : 'Enregistrer le compte-rendu'}
        </Button>
        <Button type="button" variant="outlined" onClick={() => navigate(-1)}>
          Annuler
        </Button>
      </div>
    </form>
  );
};

export default DeclarePastInterview;